import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { apiUrl } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

const ERROR_MESSAGES: Record<string, string> = {
  domain_not_allowed:
    'This Google account isn\u2019t authorized to sign in. Please use your @figureit.ai account, or contact an admin for access.',
};

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();

  // If user lands here from an invite link, surface it.
  const url = new URL(window.location.href);
  const inviteToken = url.searchParams.get('invite');
  const errorCode = url.searchParams.get('error');
  const errorMessage = errorCode ? (ERROR_MESSAGES[errorCode] ?? null) : null;

  useEffect(() => {
    if (inviteToken) sessionStorage.setItem('pendingInvite', inviteToken);
  }, [inviteToken]);

  if (isLoading) {
    return <div className="h-app flex items-center justify-center text-text2">Loading…</div>;
  }
  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <div className="h-app flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-[400px] bg-surface border border-border rounded-xl p-6 sm:p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold">
            <span className="text-accent">Figureit</span> CRM
          </h1>
          <p className="text-text2 text-sm mt-1">Sign in with your Google account to continue.</p>
        </div>
        {errorMessage && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-red/40 bg-red/10 px-3 py-2 text-sm text-red"
          >
            {errorMessage}
          </div>
        )}
        <a
          href={apiUrl('/api/auth/google')}
          className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 rounded-md py-2.5 px-4 font-medium hover:bg-gray-100 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path
              fill="#FFC107"
              d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.426 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
              fill="#FF3D00"
              d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
              fill="#4CAF50"
              d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.402 0-9.94-3.071-11.281-7.946l-6.522 5.025C9.604 39.556 16.227 44 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
          </svg>
          Continue with Google
        </a>
        <p className="text-center text-text3 text-xs mt-6">
          By signing in you agree to allow Figureit to read your basic Google profile.
        </p>
      </div>
    </div>
  );
}
